import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, getDocs, addDoc, deleteDoc, doc, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { useNotices } from '../../context/NoticeContext';
import useRole from '../../hooks/useRole';
import { format } from 'date-fns';
import { IoAdd, IoClose, IoImageOutline, IoChevronBack } from 'react-icons/io5';

const ACCENT = "#e8664a";

const BoardPage = () => {
  const { userData } = useAuth();
  const { isAdmin } = useRole();
  const { notices } = useNotices();
  const [tab, setTab] = useState('notice');
  const [sharePosts, setSharePosts] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '' });
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (tab === 'share') loadSharePosts();
  }, [tab]);

  const loadSharePosts = async () => {
    setShareLoading(true);
    try {
      const snap = await getDocs(collection(db, 'posts'));
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.type === 'share')
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setSharePosts(list);
    } catch (err) {
      console.error('게시글 로드 실패:', err);
    } finally {
      setShareLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5);
    setImages(files);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.content) return;
    setSubmitting(true);
    try {
      const imageUrls = [];
      for (const file of images) {
        const storageRef = ref(storage, `posts/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        imageUrls.push(url);
      }

      await addDoc(collection(db, 'posts'), {
        uid: userData.uid,
        authorName: userData.companyName || userData.name,
        type: isAdmin && tab === 'notice' ? 'notice' : 'share',
        title: form.title,
        content: form.content,
        images: imageUrls,
        comments: [],
        createdAt: serverTimestamp(),
      });

      setForm({ title: '', content: '' });
      setImages([]);
      setShowForm(false);
      if (tab === 'share') loadSharePosts();
    } catch (err) {
      console.error('게시글 작성 실패:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('게시글을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'posts', id));
      if (tab === 'share') loadSharePosts();
    } catch (err) {
      console.error('삭제 실패:', err);
    }
  };

  const canWrite = (tab === 'notice' && isAdmin) || tab === 'share';

  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate(-1)}><IoChevronBack size={24} /></BackBtn>
        <HeaderTitle>게시판</HeaderTitle>
        <Spacer />
      </Header>


      {tab === 'notice' ? (
        notices.length === 0 ? (
          <EmptyText>공지사항이 없습니다</EmptyText>
        ) : (
          <PostList>
            {notices.map(n => (
              <PostCard key={n.id} $pinned={n.pinned}>
                {n.pinned && <PinnedBadge>중요</PinnedBadge>}
                <PostHeader>
                  <PostDate>{n.createdAt?.toDate ? format(n.createdAt.toDate(), 'yyyy.MM.dd') : ''}</PostDate>
                </PostHeader>
                <PostTitle>{n.title}</PostTitle>
                <PostContent>{n.content}</PostContent>
                {n.category && <CatTag>{n.category}</CatTag>}
              </PostCard>
            ))}
          </PostList>
        )
      ) : shareLoading ? (
        <EmptyText>불러오는 중...</EmptyText>
      ) : sharePosts.length === 0 ? (
        <EmptyText>작업 공유가 없습니다</EmptyText>
      ) : (
        <PostList>
          {sharePosts.map(post => (
            <PostCard key={post.id}>
              <PostHeader>
                <PostAuthor>{post.authorName}</PostAuthor>
                <PostDate>{post.createdAt?.toDate ? format(post.createdAt.toDate(), 'yyyy.MM.dd') : ''}</PostDate>
              </PostHeader>
              <PostTitle>{post.title}</PostTitle>
              <PostContent>{post.content}</PostContent>
              {post.images?.length > 0 && (
                <ImageRow>
                  {post.images.map((url, i) => (
                    <PostImage key={i} src={url} alt="" />
                  ))}
                </ImageRow>
              )}
              {(post.uid === userData?.uid || isAdmin) && (
                <PostActions>
                  <DeletePostBtn onClick={() => handleDelete(post.id)}>삭제</DeletePostBtn>
                </PostActions>
              )}
            </PostCard>
          ))}
        </PostList>
      )}

      {canWrite && (
        <FloatingBtn onClick={() => setShowForm(true)}>
          <IoAdd size={28} />
        </FloatingBtn>
      )}

      {showForm && (
        <FormOverlay onClick={() => setShowForm(false)}>
          <FormSheet onClick={e => e.stopPropagation()}>
            <SheetHandle />
            <FormTitle>{tab === 'notice' ? '공지사항 작성' : '작업 공유'}</FormTitle>

            <FormLabel>제목 *</FormLabel>
            <FormInput
              placeholder="제목을 입력하세요"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
            />

            <FormLabel>내용 *</FormLabel>
            <FormTextarea
              placeholder="내용을 입력하세요"
              value={form.content}
              onChange={e => setForm({ ...form, content: e.target.value })}
              rows={5}
            />

            {tab === 'share' && (
              <>
                <FormLabel>사진 첨부 (최대 5장)</FormLabel>
                <ImageUploadBtn>
                  <IoImageOutline size={20} />
                  <span>사진 선택 {images.length > 0 && `(${images.length})`}</span>
                  <HiddenInput type="file" accept="image/*" multiple onChange={handleImageChange} />
                </ImageUploadBtn>
              </>
            )}

            <FormSubmitBtn onClick={handleSubmit} disabled={submitting || !form.title || !form.content}>
              {submitting ? '등록 중...' : '게시글 등록'}
            </FormSubmitBtn>
          </FormSheet>
        </FormOverlay>
      )}
    </Container>
  );
};

export default BoardPage;

const Container = styled.div`
  min-height: 100vh;
  background: #F5F7FA;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #F0F0EC;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  color: #1E2A32;
  padding: 4px;
`;

const HeaderTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;

const Spacer = styled.div`width: 32px;`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #f0f0f0;
`;

const TabBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  color: ${({ $active }) => $active ? 'white' : '#666'};
  background: ${({ $active }) => $active ? ACCENT : '#f5f5f5'};
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #999;
  text-align: center;
  padding: 60px 20px;
`;

const PostList = styled.div`
  padding: 12px 16px;
`;

const PostCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  position: relative;
  ${({ $pinned }) => $pinned && `
    background: #FFF9F7;
  `}
`;

const PinnedBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #E85830;
  padding: 2px 8px;
  border-radius: 6px;
`;

const PostHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const PostAuthor = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${ACCENT};
`;

const PostDate = styled.span`
  font-size: 12px;
  color: #999;
`;

const PostTitle = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const PostContent = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  white-space: pre-line;
`;

const ImageRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  overflow-x: auto;
`;

const PostImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
`;

const PostActions = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
`;

const DeletePostBtn = styled.button`
  font-size: 12px;
  color: #999;
  &:hover { color: #EF5350; }
`;

const CatTag = styled.span`
  display: inline-block;
  margin-top: 8px;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 10px;
  background: #EDF2EB;
  color: #4A6741;
  font-weight: 600;
`;

const FloatingBtn = styled.button`
  position: fixed;
  bottom: 80px;
  right: calc(50% - 220px);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${ACCENT};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(232,102,74,0.3);
  z-index: 50;
`;

const FormOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const FormSheet = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 12px 24px 32px;
  overflow-y: auto;
  animation: slideUp 0.25s ease-out;
  @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  margin: 0 auto 16px;
`;

const FormTitle = styled.p`
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
  margin-top: 12px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  &:focus { border-color: ${ACCENT}; }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  resize: none;
  &:focus { border-color: ${ACCENT}; }
`;

const ImageUploadBtn = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 1px dashed #ddd;
  border-radius: 8px;
  color: #888;
  font-size: 14px;
  cursor: pointer;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FormSubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 20px;
  border-radius: 28px;
  background: ${ACCENT};
  color: white;
  font-size: 15px;
  &:disabled { opacity: 0.5; }
`;
